import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { Layers, Search, Plus, Trash2, RefreshCw, Loader2, ImagePlus, FolderUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { API, authFetch } from "@/App";
import { AuthImage } from "./AuthImage";
import { FilePickButton } from "./FilePickButton";
import { useFabricLibrary, refreshFabricLibrary, swatchImageUrl, responseError } from "./useFabricLibrary";
import { normalizeFabricPart, makeFabricKey } from "./fabricUtils";
import { postSwatch } from "./swatchUpload";
import { FabricFolderImport } from "./FabricFolderImport";
import { FabricCollectionAliases } from "./FabricCollectionAliases";

async function uploadSwatch(collection, code, file) {
  await postSwatch(collection, code, file);
  await refreshFabricLibrary();
}

function SwatchCard({ swatch, idx, onReplace, onDelete }) {
  return (
    <div className="rounded-lg border border-zinc-200 overflow-hidden bg-white hover:shadow-md transition-shadow" data-testid={`fabric-swatch-${idx}`}>
      <AuthImage src={swatchImageUrl(swatch)} alt={swatch.code} className="w-full aspect-[5/3] object-cover" />
      <div className="p-2.5">
        <p className="font-bold text-zinc-900 tracking-wide">{swatch.code}</p>
        <p className="text-xs text-zinc-600 truncate" title={swatch.collection}>{swatch.collection || "Sans collection"}</p>
        <p className="text-[10px] text-zinc-400 mt-1">{swatch.source === "email" ? "Depuis un email" : "Ajout manuel"} · {new Date(swatch.created_at).toLocaleDateString("fr-FR")}</p>
        <div className="flex gap-1.5 mt-2">
          <FilePickButton accept="image/*" onFile={(f) => onReplace(swatch, f)} testId={`fabric-swatch-replace-${idx}`} className="h-7 px-2 text-xs gap-1 flex-1" title="Remplacer la photo">
            <RefreshCw className="w-3 h-3" />Remplacer
          </FilePickButton>
          <Button variant="outline" size="sm" className="h-7 px-2 text-red-600 hover:bg-red-50" onClick={() => onDelete(swatch)} data-testid={`fabric-swatch-delete-${idx}`} title="Supprimer"><Trash2 className="w-3 h-3" /></Button>
        </div>
      </div>
    </div>
  );
}

export function FabricSwatchLibrary() {
  const library = useFabricLibrary();
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ collection: "", code: "", file: null });
  const [saving, setSaving] = useState(false);
  const [folderOpen, setFolderOpen] = useState(false);

  const list = useMemo(() => {
    const q = normalizeFabricPart(search);
    return q ? library.list.filter((s) => s.key.includes(q)) : library.list;
  }, [library.list, search]);
  const exists = form.code.trim() && library.byKey.has(makeFabricKey(form.collection, form.code));

  const add = async () => {
    setSaving(true);
    try {
      await uploadSwatch(form.collection, form.code, form.file);
      toast.success(`Toile ${normalizeFabricPart(form.code)} enregistrée`);
      setForm({ collection: "", code: "", file: null });
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const replace = async (s, file) => {
    try { await uploadSwatch(s.collection, s.code, file); toast.success(`Photo de ${s.code} remplacée`); } catch (e) { toast.error(e.message); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Supprimer la photo de la toile ${s.collection} ${s.code} ?`)) return;
    const res = await authFetch(`${API}/fabric-swatches/${s.swatch_id}`, { method: "DELETE" });
    if (!res.ok) { toast.error(await responseError(res)); return; }
    await refreshFabricLibrary();
    toast.success("Toile supprimée de la bibliothèque");
  };

  return (
    <div className="bg-white rounded-lg border border-zinc-200 p-6" data-testid="fabric-library-section">
      <div className="flex items-start justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <Layers className="w-6 h-6 text-orange-500" />
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 font-['Manrope']">Bibliothèque des toiles <span className="text-sm font-normal text-zinc-400" data-testid="fabric-library-count">({library.list.length})</span></h2>
            <p className="text-sm text-zinc-500">Une photo de référence par COLLECTION + CODE, imprimée sur les fiches de production.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setFolderOpen(true)} className="h-9 gap-1.5" data-testid="fabric-folder-import-btn"><FolderUp className="w-4 h-4" />Importer un dossier</Button>
          <div className="relative w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un code…" className="pl-9 h-9" data-testid="fabric-library-search" />
          </div>
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap p-3 mb-5 rounded-lg bg-zinc-50 border border-zinc-200">
        <div className="flex-1 min-w-[200px]"><label className="text-xs font-medium text-zinc-600">Collection</label><Input value={form.collection} onChange={(e) => setForm((f) => ({ ...f, collection: e.target.value }))} placeholder="S&P SELECTION 2025" className="h-9 bg-white" data-testid="fabric-add-collection" /></div>
        <div className="w-32"><label className="text-xs font-medium text-zinc-600">Code</label><Input value={form.code} onChange={(e) => setForm((f) => ({ ...f, code: e.target.value }))} placeholder="00197" className="h-9 bg-white" data-testid="fabric-add-code" /></div>
        <FilePickButton accept="image/*" onFile={(file) => setForm((f) => ({ ...f, file }))} testId="fabric-add-file" className="h-9 gap-1.5 max-w-[200px]">
          <ImagePlus className="w-4 h-4" /><span className="truncate">{form.file ? form.file.name : "Choisir la photo"}</span>
        </FilePickButton>
        <Button onClick={add} disabled={saving || !form.code.trim() || !form.file} className="h-9 gap-1.5 bg-orange-500 hover:bg-orange-600" data-testid="fabric-add-submit">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}{exists ? "Remplacer" : "Ajouter"}
        </Button>
      </div>

      <FabricCollectionAliases library={library} />

      {list.length === 0 ? (
        <p className="text-center text-zinc-400 py-8 text-sm" data-testid="fabric-library-empty">{library.list.length ? "Aucune toile ne correspond à la recherche" : "Aucune toile pour l'instant — ajoutez-en ici, depuis la fenêtre CONTENT du tableau ou via un email de commande."}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {list.map((s, i) => <SwatchCard key={s.swatch_id} swatch={s} idx={i} onReplace={replace} onDelete={remove} />)}
        </div>
      )}
      <FabricFolderImport open={folderOpen} onClose={() => setFolderOpen(false)} />
    </div>
  );
}
